import type { GameEndReason } from '@chess-uno/shared-protocol'
import type { Messages } from './fr.js'

/** Plural of a counted word, Spanish rule (only 1 is singular). */
function n(count: number, word: string, plural = `${word}s`): string {
  return `${count} ${count === 1 ? word : plural}`
}

export const es: Messages = {
  common: {
    back: 'Volver',
    home: 'Inicio',
    cancel: 'Cancelar',
    loading: 'Cargando…',
    error: 'Error',
    yes: 'Sí',
    no: 'No',
    armies: { w: 'Blancas', b: 'Negras' },
    tiers: { bronze: 'Bronce', silver: 'Plata', gold: 'Oro', platinum: 'Platino', diamond: 'Diamante' },
  },

  modes: {
    purist: {
      name: 'Purista',
      tagline: 'Jaque mate',
      desc: 'Como en el ajedrez: nunca se deja al rey en jaque, el rey no se captura y se gana por jaque mate.',
      note: 'Modo Purista: no puedes dejar a tu rey en jaque; se gana por jaque mate.',
    },
    capture: {
      name: 'Caza del rey',
      tagline: 'Captura del rey',
      desc: 'Las reglas originales: puedes dejar a tu rey atacado, y se gana capturándolo.',
      note: 'Modo Caza del rey: un rey puede quedar atacado; se gana capturándolo.',
    },
  },

  campaign: {
    eyebrow: 'Reto de noviembre',
    title: 'Beat the machine',
    text: 'Gana al ordenador en nivel Medio y comparte tu victoria en Instagram: cada lunes sorteamos una tarjeta regalo de 20 €.',
    link: 'Todos los detalles en Instagram',
    dismiss: 'Ocultar',
  },

  language: {
    names: { fr: 'Français', en: 'English', es: 'Español', de: 'Deutsch', hi: 'हिन्दी' },
    menu: 'Idioma',
    hint: 'Este sitio también está disponible en español.',
    hintLink: 'Versión en español',
    dismiss: 'Cerrar',
  },

  chrome: {
    homeAria: 'Chess-Uno, inicio',
    rules: 'Reglas',
    viewProfile: 'Ver mi perfil',
    soundOn: 'Activar el sonido',
    soundOff: 'Desactivar el sonido',
    logout: 'Cerrar sesión',
    legal: 'Aviso legal',
    privacy: 'Privacidad',
    instagram: 'Chess-Uno en Instagram',
    facebook: 'Chess-Uno en Facebook',
  },

  feedback: {
    link: 'Opiniones y sugerencias',
    title: 'Tu opinión cuenta',
    lead: '¿Una idea, un error, un cumplido? Basta con un mensaje: lo lee el editor del juego.',
    close: 'Cerrar',
    kindLabel: 'Tema',
    kinds: { idea: 'Idea', bug: 'Error', praise: 'Me encanta', other: 'Otro' },
    messageLabel: 'Tu mensaje',
    placeholders: {
      idea: '¿Qué te gustaría encontrar en Chess-Uno?',
      bug: '¿Qué ha pasado? ¿En qué página y con qué dispositivo?',
      praise: '¿Qué es lo que más te gusta?',
      other: 'Escribe lo que quieras decirnos.',
    },
    contactLabel: 'Para responderte (opcional)',
    contactPlaceholder: 'Correo electrónico o usuario de Telegram',
    fine: (privacy) => (
      <>
        Tu mensaje lo lee el editor, junto con tu nombre de usuario si has iniciado sesión ({privacy('privacidad')}).
      </>
    ),
    send: 'Enviar',
    sending: 'Enviando…',
    thanksTitle: '¡Gracias!',
    thanksText: 'Tu mensaje ha llegado. Ayuda a mejorar el juego.',
    rateGame: '¿Te ha gustado esta partida?',
    starLabel: (count) => `${count} de 5`,
    rated: '¡Gracias por tu valoración!',
    tellMore: 'Cuéntanos por qué',
  },

  toast: {
    aria: (name) => `Desafío de ${name}`,
    challengesYou: (name) => (
      <>
        <strong>{name}</strong> te desafía
      </>
    ),
    accept: 'Aceptar el duelo',
    later: 'Más tarde',
  },

  auth: {
    login: 'Iniciar sesión',
    register: 'Crear una cuenta',
    username: 'Nombre de usuario',
    password: 'Contraseña',
    registerFine: (privacy) => (
      <>
        Nombre de usuario de 3 a 20 caracteres, contraseña de 6 caracteres como mínimo. Tu nombre de usuario y tus resultados aparecen en la
        clasificación pública ({privacy('privacidad')}).
      </>
    ),
    submitRegister: 'Crear mi cuenta',
    submitLogin: 'Iniciar sesión',
  },

  landing: {
    eyebrow: 'Chess-Uno · ajedrez y cartas en línea',
    title: 'Juega al ajedrez con cartas.',
    titleEm: 'Cada turno cambia la partida.',
    lead:
      'En Chess-Uno juegas al ajedrez con cartas que se revelan a lo largo de la partida. Cada turno cambia tus opciones: número de movimientos, peones disponibles o ejército que controlas. Gana por jaque mate, o capturando al rey rival en el modo Caza del rey. Desafía a un amigo, encuentra un rival clasificado o entrénate contra el ordenador.',
    readRules: 'Leer las reglas',
    leaderboard: 'Clasificación',
    howEyebrow: 'Cómo se juega',
    howTitle: 'Una partida de ajedrez, con cartas que cambian las jugadas',
    howP1:
      'El tablero y las piezas se mueven como en el ajedrez. Al principio de cada turno se roba una carta y se revela a todos: puede dar varios movimientos, hacer perder un turno, devolver peones o intercambiar los ejércitos. Dos modos: Purista, donde se gana por jaque mate, y Caza del rey, donde se gana capturando al rey rival.',
    howP2:
      'No guardas ninguna carta en la mano. Una carta numérica te da tantos movimientos como su valor; puedes detener esa serie después del primer movimiento. Las cartas +2 y +4 permiten volver a colocar peones capturados. Con Inversión, los jugadores intercambian sus ejércitos sin cambiar la posición de las piezas. Un Joker permite que una pieza se mueva como una dama durante el turno. Los dos jugadores ven siempre la carta revelada.',
    discoverRules: 'Descubre las reglas de Chess-Uno',
    playEyebrow: 'Jugar en línea',
    playTitle: 'Con amigos, en partidas clasificadas o contra el ordenador',
    playP1:
      'Crea un duelo privado y comparte su enlace, únete a la cola para una partida clasificada con Elo, o prueba las cartas contra el ordenador. Las partidas contra el ordenador no cambian tu clasificación.',
    playP2:
      'Con una cuenta, puedes invitar a un amigo con un enlace privado o buscar un rival automáticamente. Las partidas terminadas entre dos cuentas actualizan tu Elo; la clasificación muestra también los resultados recientes y tus enfrentamientos directos. Para descubrir los efectos sin nada en juego, empieza un entrenamiento contra el ordenador. Antes de un duelo privado, puedes elegir qué cartas hay en el mazo y fijar el valor máximo de las cartas numéricas.',
    heroAlt: 'Un peón de ajedrez delante de unas cartas',
    faqEyebrow: 'Preguntas frecuentes',
    faqTitle: 'Todo sobre Chess-Uno',
    faq: [
      {
        q: '¿Qué es Chess-Uno?',
        a: 'Chess-Uno es una variante del ajedrez a la que puedes jugar gratis en línea, en el navegador. Se juega en un tablero clásico con las piezas de siempre, pero al principio de cada turno se roba una carta y se revela a los dos jugadores: determina lo que el jugador puede hacer durante ese turno.',
      },
      {
        q: '¿Cómo se gana una partida?',
        a: 'Depende del modo elegido antes de la partida. En el modo Purista, como en el ajedrez, nunca puedes dejar a tu rey en jaque y se gana por jaque mate: cuando el rival no puede salir del jaque con la carta que acaba de robar. En el modo Caza del rey, puedes dejar a tu rey atacado, y se gana capturando al rey rival.',
      },
      {
        q: '¿Qué hacen las cartas?',
        a: 'Una carta numérica da tantos movimientos como su valor (de 1 a 5 por defecto). «Pierde turno» hace perder el turno. Las cartas +2 y +4 devuelven peones capturados para volver a colocarlos en el tablero. La Inversión intercambia los ejércitos de los dos jugadores. El Joker permite mover una pieza como una dama. Ningún jugador guarda cartas en la mano.',
      },
      {
        q: '¿Se puede jugar solo?',
        a: 'Sí, contra el ordenador, en nivel Fácil o Medio. Estas partidas de entrenamiento no cuentan para la clasificación. También puedes desafiar a un amigo con un enlace privado o encontrar un rival en la cola de partidas clasificadas.',
      },
      {
        q: '¿Cómo funciona la clasificación?',
        a: 'Cada jugador empieza con 1000 de Elo. Solo cuentan para la clasificación las partidas terminadas entre dos cuentas. Los rangos van de Bronce a Diamante (1400 de Elo o más), y los enfrentamientos directos entre jugadores son públicos.',
      },
      {
        q: '¿Hay que pagar o instalar algo?',
        a: 'No. Chess-Uno es gratuito y se juega en el navegador, tanto en el ordenador como en el móvil. Solo tienes que crear una cuenta con un nombre de usuario y una contraseña, sin dirección de correo electrónico.',
      },
      {
        q: '¿Chess-Uno tiene relación con el juego UNO?',
        a: 'No. Chess-Uno es un juego independiente, que no está afiliado, respaldado ni patrocinado por Mattel, titular de la marca UNO.',
      },
    ],
  },

  home: {
    greeting: (hour) => (hour >= 5 && hour < 12 ? 'Buenos días' : hour >= 12 && hour < 20 ? 'Buenas tardes' : 'Buenas noches'),
    play: 'Jugar',
    ranked: 'Partida clasificada',
    rankedDetail: 'Rival encontrado automáticamente',
    challengeFriend: 'Desafiar a un amigo',
    challengeFriendDetail: 'Enlace privado para compartir',
    join: 'Unirse',
    joinDetail: 'Con un código de invitación',
    training: 'Entrenamiento',
    trainingDetail: 'Contra la IA, sin clasificar',
    tournament: 'Torneo',
    tournamentDetail: 'Eliminación directa, con un código',
    tournamentOngoing: (name) => `Torneo en curso: ${name}`,
    tournamentOpen: (name) => `Inscripciones abiertas: ${name}`,
    rules: 'Reglas del juego',
    waitingGame: 'Partida esperando a un rival',
    ongoingAgainst: (name) => `Partida en curso contra ${name}`,
    resume: 'Continuar',
    leaderboard: 'Clasificación general',
    recentResults: 'Últimos resultados',
    headToHead: 'Cara a cara',
    rivalries: 'Grandes rivalidades',
  },

  join: {
    eyebrow: 'Invitación',
    title: 'Unirse a una partida',
    lead: 'Introduce el código que te ha enviado tu rival.',
    code: 'Código de invitación',
    submit: 'Unirse a la partida',
  },

  invite: {
    challengesYou: (name) => (
      <>
        <strong>{name}</strong> te desafía
      </>
    ),
    invited: 'Te han invitado a un duelo',
  },

  gate: {
    title: 'Iniciar sesión',
    lead: 'Inicia sesión para jugar y subir en la clasificación.',
  },

  queue: {
    searching: 'Buscando un rival…',
    chooseTitle: 'Elige tu modo',
    chooseLead: 'Te enfrentarás a un jugador que haya elegido el mismo modo. La clasificación Elo es común a los dos modos.',
    changeMode: 'Cambiar de modo',
    eyebrow: 'Partida clasificada',
    lead: 'La partida empieza en cuanto un jugador se une a la cola.',
  },

  competition: {
    justNow: 'ahora mismo',
    minutesAgo: (m) => `hace ${m} min`,
    hoursAgo: (h) => `hace ${h} h`,
    daysAgo: (d) => `hace ${n(d, 'día')}`,
    tierTitle: (label) => `Rango ${label}`,
    streakTitle: (count) => n(count, 'victoria seguida', 'victorias seguidas'),
    form: { W: 'Victoria', L: 'Derrota', D: 'Tablas' },
    formAria: (items) => `Forma: ${items.join(', ')}`,
    yourRanking: 'Tu clasificación',
    outOf: (count) => `de ${n(count, 'jugador clasificado', 'jugadores clasificados')}`,
    unranked: 'Sin clasificar — juega una partida clasificada',
    pointsFor: (missing, label) => `Te faltan ${missing} puntos para ${label}`,
    maxTier: 'Rango máximo',
    pointsBefore: (missing, label) => (
      <>
        <b>{missing}</b> puntos para {label}
      </>
    ),
    maxTierCaption: 'Rango máximo — defiende tu puesto',
    wins: 'Victorias',
    losses: 'Derrotas',
    draws: 'Tablas',
    winRate: 'Efectividad',
    percent: (value) => `${value} %`,
    streak: 'Racha',
    best: (count) => `récord ${count}`,
    leaderboardEmpty: 'Todavía no hay nadie clasificado. Gana una partida contra un amigo para abrir la clasificación.',
    you: 'tú',
    record: (w, l, d) => `${w} V · ${l} D${d ? ` · ${d} T` : ''}`,
    challenge: 'Desafiar',
    taunts: {
      never: 'Ningún enfrentamiento todavía: abre tú el marcador.',
      twoLosses: 'Dos derrotas seguidas. Toca revancha.',
      dominate: 'Dominas este duelo.',
      lead: 'Vas por delante, pero no hay nada decidido.',
      tied: 'Empate perfecto.',
      escape: 'Este duelo se te escapa.',
      behind: 'Vas por detrás.',
    },
    rivalsEmpty: 'Todavía no tienes enfrentamientos. Desafía a un jugador de la clasificación.',
    revenge: 'Revancha',
    replay: 'Volver a jugar',
    drawsShort: (d) => `${d} T`,
    rivalriesEmpty: 'Una rivalidad aparece en cuanto dos jugadores se han enfrentado dos veces.',
    rivalryMeta: (games, draws) => `${n(games, 'partida')}${draws ? ` · ${n(draws, 'empate')}` : ''}`,
    recentEmpty: 'Todavía no hay partidas clasificadas.',
    and: 'y',
    drew: 'hacen tablas',
    beat: 'vence a',
    reasons: {
      king_captured: 'rey capturado',
      checkmate: 'jaque mate',
      resign: 'abandono',
      abandon: 'desconexión',
      draw_agreed: 'tablas aceptadas',
      no_move_limit: 'tablas por inactividad',
    },
  },

  settings: {
    decrease: 'Disminuir',
    increase: 'Aumentar',
    maxNumberAria: 'Valor máximo de las cartas numéricas',
    keepCards: (max) => `Conservar las cartas del 1 al ${max}`,
    eyebrowBot: 'Entrenamiento · sin clasificar',
    eyebrowPrivate: 'Partida privada · clasificada',
    title: 'Ajustes de la partida',
    leadBot: 'Configura el rival y el mazo, y empieza la partida.',
    leadPrivate: 'Configura el mazo; se crea un enlace de invitación para tu rival.',
    opponent: 'Rival',
    opponentHint: 'Niveles experimentales: la IA dispone de más o menos tiempo para pensar.',
    easy: 'Fácil',
    easyDesc: 'Anticipa una jugada del rival.',
    medium: 'Medio',
    mediumDesc: 'Anticipa dos jugadas y piensa más tiempo.',
    side: 'Tu bando',
    sideHint: 'Una Inversión puede intercambiar los ejércitos durante la partida.',
    whiteDesc: 'Empiezas tú',
    blackDesc: 'Empieza la IA',
    random: 'Al azar',
    randomSummary: 'Sorteo',
    rules: 'Reglas',
    rulesHint: 'Dos formas de jugar: como en el ajedrez, o cazando al rey.',
    deck: 'Mazo',
    deckHint: 'Una carta numérica da tantos movimientos como su valor.',
    numberCards: 'Cartas numéricas',
    fromOneTo: (max) => (
      <>
        del 1 al <b>{max}</b>
      </>
    ),
    jokers: 'Jokers',
    jokersDesc: 'Un movimiento, en el que cualquier pieza se mueve como una dama.',
    advanced: 'Reglas avanzadas',
    advancedHint: 'Recuperación de peones, salvaguarda, tablas.',
    plus2: 'Peones devueltos por un +2',
    plus4: 'Peones devueltos por un +4',
    drawAfter: 'Tablas tras N turnos sin movimiento',
    safeguard: 'Salvaguarda de las series',
    safeguardDesc: 'Una serie se detiene en cuanto el rey rival está atacado.',
    summary: 'Resumen',
    mode: 'Modo',
    modeBot: 'Contra la IA',
    modePrivate: 'Enlace privado',
    level: 'Nivel',
    camp: 'Bando',
    deckSize: (count) => n(count, 'carta'),
    numbers: 'Números',
    range: (max) => `del 1 al ${max}`,
    jokersIncluded: 'Jokers',
    ranking: 'Clasificación',
    unranked: 'Sin clasificar',
    eloAtStake: 'Elo en juego',
    start: 'Empezar la partida',
    create: 'Crear la partida',
    locked: 'Las reglas quedan fijadas al empezar la partida.',
  },

  game: {
    drawAgreed: 'Tablas (de mutuo acuerdo).',
    drawIdle: 'Tablas (turnos sin movimiento).',
    result: (mine, winner, reason) => {
      const why: Partial<Record<GameEndReason, string>> = {
        king_captured: 'captura del rey',
        checkmate: 'jaque mate',
        resign: 'abandono',
        abandon: 'desconexión prolongada',
      }
      return `${mine ? 'Victoria' : 'Derrota'} — ${winner} gana por ${why[reason] ?? reason}.`
    },
    headToHead: 'Cara a cara',
    firstDuel: 'Primer duelo',
    youLead: 'Vas por delante',
    leads: (name) => `${name} va por delante`,
    tied: 'Empate',
    training: (level) => `Partida de entrenamiento contra la IA (nivel ${level}): sin clasificar.`,
    computing: 'Calculando la clasificación…',
    promotion: (tier) => `Ascenso: rango ${tier}`,
    demotion: (tier) => `Descenso a ${tier}… toca revancha.`,
    recordAgainst: (name) => `Balance contra ${name}:`,
    streak: (count) => n(count, 'victoria seguida', 'victorias seguidas'),
    backHome: 'Volver al inicio',
    privateGame: 'Partida privada',
    waiting: 'Esperando a un rival…',
    shareLink: 'Comparte este enlace:',
    copied: '¡Copiado!',
    copyLink: 'Copiar el enlace',
    inviteText: (name) => `¡${name} te desafía en Chess-Uno! Acepta el duelo:`,
    messageCopied: '¡Mensaje copiado!',
    sent: '¡Enviado!',
    sendChallenge: 'Enviar el desafío',
    code: 'Código:',
    notified: 'El jugador desafiado también recibe una notificación si está conectado.',
    loadingGame: 'Cargando la partida…',
    connecting: '(conectando con el servidor…)',
    connected: 'Conectado',
    reconnecting: 'Reconectando…',
    acceptDraw: 'Aceptar las tablas',
    decline: 'Rechazar',
    offerDraw: 'Ofrecer tablas',
    offerDrawShort: 'Tablas',
    offerDrawConfirm: '¿Ofrecer tablas a tu rival?',
    acceptDrawShort: 'Aceptar',
    resignConfirm: '¿Abandonar la partida?',
    resign: 'Abandonar',
    resultAria: 'Resultado',
    rematchOffered: (name) => `${name} propone una revancha.`,
    rematchWaiting: 'Revancha propuesta: esperando al rival…',
    rematch: 'Revancha',
    closeResult: 'Cerrar para ver el tablero',
    showResult: 'Ver el resultado',
    verdicts: { win: 'Victoria', loss: 'Derrota', draw: 'Tablas' },
    winText: (opponent, gain, rating) => `He ganado a ${opponent} en Chess-Uno (+${gain} Elo, ${rating}). ¿Quién es el siguiente?`,
    shared: '¡Compartido!',
    shareWin: 'Compartir mi victoria',
  },

  panels: {
    phases: {
      turn_start: 'Inicio del turno',
      draw: 'Robo',
      effect_choice: 'Elección del efecto (Joker)',
      place_pawns: 'Colocación de los peones recuperados',
      moves: 'Movimientos',
      turn_end: 'Fin del turno',
      game_over: 'Partida terminada',
    },
    playsArmy: (army) => `Juega con ${army === 'w' ? 'blancas' : 'negras'}`,
    chooseMoves: (you) => (you ? 'Elige tu número de movimientos' : 'elige su número de movimientos'),
    placePawns: (you, count) =>
      you ? `Vuelve a colocar ${n(count, 'peón', 'peones')} (casillas verdes)` : `vuelve a colocar ${n(count, 'peón', 'peones')}`,
    joker: (you) => (you ? 'Joker: mueve cualquier pieza como una dama' : 'Joker: mueve una pieza como una dama'),
    moreMoves: (you, count) =>
      you ? `Te ${count === 1 ? 'queda' : 'quedan'} ${n(count, 'movimiento')} (o termina la serie)` : `le ${count === 1 ? 'queda' : 'quedan'} ${n(count, 'movimiento')}`,
    playMoves: (you, count) => (you ? `Juega ${n(count, 'movimiento')}` : `juega ${n(count, 'movimiento')}`),
    pipsAria: (made, total) => `${made} de ${n(total, 'movimiento jugado', 'movimientos jugados')}`,
    yourTurn: 'Te toca',
    turnOf: (name) => `Turno de ${name}`,
    check: '¡Jaque!',
    go: '¡Adelante!',
    gameStarts: 'Empieza la partida',
    drawing: (you, summary) => `${you ? 'Robas' : 'roba'}: ${summary}`,
    inCheckYou: (mode) =>
      mode === 'purist' ? 'Tu rey está en jaque: defiéndete con tu carta.' : 'Tu rey está atacado: defiéndete con tu carta.',
    inCheckOpponent: (mode) =>
      mode === 'purist' ? 'Rey rival en jaque: si no se defiende, es jaque mate.' : 'Rey rival atacado: si no se defiende, será capturado.',
    turnNumber: (turn) => `Turno ${turn}`,
    discardTitle: (owner, count) => `Descarte de ${owner}: ${n(count, 'carta')}`,
    discard: 'Descarte',
    seatAria: (name) => `Jugador ${name}`,
    connected: 'Conectado',
    disconnected: 'Desconectado',
    you: '(tú)',
    ai: 'IA',
    eloTitle: (tier) => `Clasificación Elo · ${tier}`,
    seatYourTurn: 'te toca',
    seatPlaying: 'juega',
    pawnsTitle: 'Peones capturados que un +2 o un +4 puede recuperar',
    toRecover: (count) => `${count} por recuperar`,
    turnCardAria: 'Carta del turno',
    drawPile: 'Mazo',
    youDrew: 'Has robado',
    drew: (name) => `${name} ha robado`,
    revealed: (label) => `Revelada: ${label}`,
    noCard: 'Ninguna carta',
    jokerChoiceAria: 'Elección del número de movimientos',
    jokerChoice: 'Joker: ¿cuántos movimientos este turno?',
    placementHint: (count) => `Haz clic en una casilla verde del tablero para volver a colocar un peón (${count === 1 ? 'queda' : 'quedan'} ${count}).`,
    endSeries: (count) => `Terminar la serie (${n(count, 'movimiento sin jugar', 'movimientos sin jugar')})`,
    effects: {
      number: (count) => `${n(count, 'movimiento')} por jugar.`,
      zero: 'Turno pasado sin movimiento.',
      skip: 'Pierde turno: el rival roba enseguida y juega su carta.',
      reverse: 'Inversión: los jugadores intercambian sus ejércitos; el jugador actual continúa con el nuevo.',
      plus: (card, pawns, moves) => `${card}: recupera hasta ${n(pawns, 'peón capturado', 'peones capturados')} y luego ${n(moves, 'movimiento')}.`,
      wild: 'Joker: un movimiento, con cualquier pieza que se mueve como una dama.',
    },
    reveal: {
      who: (you, name) => (you ? 'Robas' : `${name} roba`),
      moves: (count) => n(count, 'movimiento'),
      zero: 'Turno pasado',
      zeroDetail: 'Ningún movimiento este turno.',
      skip: 'Pierde el turno',
      skipDetail: (name) => `${name} roba y juega.`,
      reverse: '¡Inversión!',
      reverseDetail: 'Los jugadores intercambian sus ejércitos.',
      recovers: (count) => `Recupera ${n(count, 'peón', 'peones')}`,
      then: (count) => `Luego ${n(count, 'movimiento')}.`,
      oneMove: '1 movimiento',
      noPawn: 'Ningún peón que recuperar.',
      joker: 'Joker',
      jokerDetail: 'Una pieza a elegir se mueve como una dama.',
    },
    yourKingAttacked: 'Tu rey está atacado',
    enemyKingAttacked: 'El rey rival está atacado',
    youWin: 'Ganas',
    wins: (name) => `${name} gana`,
    pieces: { p: 'el peón', n: 'el caballo', b: 'el alfil', r: 'la torre', q: 'la dama', k: 'el rey' },
    somePiece: 'la pieza',
    deliversMate: (piece, from) => `${piece} en ${from} da jaque mate`,
    takesKing: (piece, from, to) => `${piece} en ${from} captura al rey en ${to}`,
    kingTakenOn: (square) => `El rey es capturado en ${square}`,
    checkmate: 'Jaque mate',
    kingCaptured: 'Rey capturado',
    statusAria: 'Estado de la partida',
    phase: 'Fase',
    actor: 'Jugador que debe actuar',
    movesRemaining: 'Movimientos restantes',
    movesValue: (remaining, made) => `${remaining} (realizados: ${made})`,
    idleTurns: 'Turnos sin movimiento',
    carefulKing: 'Rey prudente: el rey no puede ir a una casilla atacada.',
    kingAttacked: (army, mode) =>
      mode === 'purist'
        ? `El rey ${army === 'w' ? 'blanco' : 'negro'} está en jaque.`
        : `El rey ${army === 'w' ? 'blanco' : 'negro'} está atacado (solo informativo, no hay obligación de reaccionar).`,
    youOfferedDraw: 'Has ofrecido tablas.',
    offersDraw: (name) => `${name} ofrece tablas.`,
    log: 'Registro',
  },

  board: {
    promotionAria: 'Elección de la promoción',
    promotion: 'Promoción:',
    pieces: { q: 'Dama', r: 'Torre', b: 'Alfil', n: 'Caballo' },
  },

  card: {
    back: 'Dorso de la carta',
  },

  tournament: {
    eyebrow: 'Torneo',
    hubTitle: 'Torneo de eliminación directa',
    hubLead: 'Cada derrota te elimina: el último jugador en pie gana el torneo.',
    joinTitle: 'Unirse a un torneo',
    joinCode: 'Código del torneo',
    joinSubmit: 'Unirse',
    createTitle: 'Organizar un torneo',
    createHint: 'Recibes un código para compartir. Tú empiezas el torneo cuando estén todos.',
    name: 'Nombre (opcional)',
    namePlaceholder: 'Torneo del viernes',
    modeLabel: 'Modo de juego',
    sizeLabel: 'Número máximo de jugadores',
    create: 'Crear el torneo',
    mine: 'Tus torneos',
    back: 'Torneos',
    defaultName: (host) => `Torneo de ${host}`,
    status: { registration: 'Inscripciones abiertas', running: 'En curso', finished: 'Terminado', cancelled: 'Cancelado' },
    playerCount: (count, max) => `${count} / ${max} jugadores`,
    hostedBy: (name) => `Organizado por ${name}`,
    open: 'Abrir',
    codeLabel: 'Código',
    copyLink: 'Copiar el enlace',
    copied: '¡Copiado!',
    share: 'Compartir',
    shared: '¡Compartido!',
    shareText: (name, host) => `${host} te invita al torneo «${name}» en Chess-Uno. Únete:`,
    players: 'Jugadores',
    host: 'Organizador',
    you: 'tú',
    join: 'Unirse al torneo',
    leave: 'Salir del torneo',
    start: 'Empezar el torneo',
    startHint: 'Se necesitan al menos 2 jugadores. El cuadro se sortea al empezar; después ya nadie puede inscribirse.',
    waitingHost: 'Esperando a que el organizador empiece el torneo…',
    cancel: 'Cancelar el torneo',
    cancelConfirm: '¿Cancelar este torneo para todos los jugadores?',
    yourMatch: (opponent) => `Tu enfrentamiento contra ${opponent} está listo`,
    play: 'Jugar',
    waitingNext: 'Próximo enfrentamiento: esperando los demás resultados…',
    byeThrough: 'Pasas directamente a la siguiente ronda.',
    eliminated: (round) => {
      const r = round.toLowerCase()
      return `Has quedado fuera en ${r === 'final' ? 'la final' : r}. Sigue el resto aquí.`
    },
    champion: 'Ganador del torneo',
    youWon: '¡Enhorabuena, ganas el torneo!',
    cancelled: 'El organizador ha cancelado este torneo.',
    bracket: 'Cuadro',
    roundName: (fromEnd) =>
      ({ 1: 'Final', 2: 'Semifinales', 3: 'Cuartos de final', 4: 'Octavos de final', 5: 'Dieciseisavos de final' })[fromEnd] ??
      `Ronda ${fromEnd}`,
    roundOne: (fromEnd) =>
      ({ 1: 'Final', 2: 'Semifinal', 3: 'Cuartos de final', 4: 'Octavos de final', 5: 'Dieciseisavos de final' })[fromEnd] ??
      `Ronda ${fromEnd}`,
    tbd: 'Por determinar',
    bye: 'Pasa directamente',
    matchStates: { waiting: 'En espera', playing: 'En curso', done: 'Terminado' },
    endedBy: {
      king_captured: 'rey capturado',
      checkmate: 'jaque mate',
      resign: 'abandono',
      abandon: 'incomparecencia',
      draw_agreed: 'tablas aceptadas',
      no_move_limit: 'tablas por inactividad',
      coin_toss: 'cara o cruz',
    },
    redraws: (count) => n(count, 'empate repetido', 'empates repetidos'),
    rulesTitle: 'Reglas del torneo',
    rules: (idleMinutes) => [
      'Eliminación directa: el perdedor de un enfrentamiento queda eliminado y el ganador pasa a la siguiente ronda.',
      'El cuadro se sortea al empezar. Si el número de jugadores no es una potencia de dos, algunos pasan la primera ronda directamente.',
      'Una partida en tablas se repite con los colores invertidos; tras tres tablas, se decide a cara o cruz.',
      `Sin revancha. Un jugador que no actúa durante ${n(idleMinutes, 'minuto')}, o que sigue desconectado, pierde la partida.`,
      'Las partidas de torneo cuentan para la clasificación Elo.',
    ],
    notFoundTitle: 'Torneo no encontrado',
    notFoundLead: 'Comprueba el código que te ha dado el organizador.',
    invited: 'Te han invitado a un torneo',
    invitedTo: (name) => (
      <>
        Te han invitado al torneo <strong>{name}</strong>
      </>
    ),
    toast: {
      aria: (name) => `Enfrentamiento de torneo: ${name}`,
      ready: (opponent) => (
        <>
          Tu enfrentamiento contra <strong>{opponent}</strong> está listo
        </>
      ),
      play: 'Jugar',
      later: 'Más tarde',
    },
    game: {
      strip: (name, round) => `${name} · ${round}`,
      rule: (idleMinutes) => `Sin revancha · ${n(idleMinutes, 'minuto')} de inactividad como máximo`,
      bracket: 'Ver el cuadro',
      backToBracket: 'Volver al cuadro',
      drawReplay: 'Tablas: el enfrentamiento se repite con los colores invertidos.',
    },
  },

  profile: {
    notFound: 'Jugador no encontrado.',
    eyebrow: 'Perfil',
    challenge: (name) => `Desafiar a ${name}`,
    shareMe: (rank, rating) => `Estoy ${rank ? `en el puesto #${rank} ` : ''}en Chess-Uno con ${rating} de Elo. ¡Ven a destronarme!`,
    shareOther: (name, rating) => `${name} tiene ${rating} de Elo en Chess-Uno. ¿Quién acepta el desafío?`,
    linkCopied: '¡Enlace copiado!',
    shared: '¡Compartido!',
    share: 'Compartir',
    yourRanking: 'Tu clasificación',
    ranking: 'Clasificación',
    youVs: (name) => `Tú contra ${name}`,
    draws: (count) => n(count, 'empate'),
    headToHead: 'Cara a cara',
    recentResults: 'Últimos resultados',
  },

  rules: {
    eyebrow: 'Reglamento',
    toc: 'Índice',
  },

  legal: {
    updated: (date) => `Última actualización: ${date}`,
  },
}
